<script setup>
import { normalizeDates } from '#shared/utils/datesTimes'
import { ContentTypeEnum, PublishedStatusEnum } from '#shared/utils/defaultDataSchema';
import LinkStatus from '~/components/content/LinkStatus.vue'

const props = defineProps({
  isShowFormat: {
    type: Boolean,
    default: true
  },
  limit: {
    type: Number,
    default: null
  },
  rootPath: {
    type: String,
    default: '/blog'
  },
  sortBy: {
    type: String,
    validator: (value) => ['date_created', 'date_modified', 'title'].includes(value),
    default: 'date_modified'
  },
  sortOrder: {
    type: String,
    validator: (value) => ['ASC', 'DESC'].includes(value),
    default: 'DESC'
  }
})

let queryName = props.rootPath.slice(1).replace(/\//g, "_") + '-posts';
if(props.sortBy !== 'date_modified') {
  queryName += `-${props.sortBy}`
}
if(props.sortOrder !== 'DESC') {
  queryName += `-${props.sortOrder}`
}
if(props.limit) {
  queryName += `-${props.limit}`
}

logger.info(`PostList: ${queryName}`)

const { data } = await useAsyncData(queryName, () => {

    const query = queryCollection('content')
      .where('path', 'LIKE', `${props.rootPath}%`)
      .where('content_type', '!=', ContentTypeEnum.Index)
      .where('content_type', '!=', ContentTypeEnum.Hub)
      .where('content_type', '!=', ContentTypeEnum.Guide)

    if(props.sortBy === 'title') {
      query.order('title', props.sortOrder)
    }

    if(import.meta.env.PROD) {
      query.where('published', '==', PublishedStatusEnum.Published)
    }

    return query.all()
  }
)

const posts = computed(() => {
  if(!data.value) return []

  const postsNormalized = [], postsListDate = []
  let lastYear = null;

  // Ensure all posts have date_created and date_modified
  for(const post of data.value) {
    postsNormalized.push(normalizeDates(post))
  }

  // for date sorts, sort posts by the specified sortBy and sortOrder
  if(props.sortBy !== 'title') {
    postsNormalized.sort((a, b) => {
      const dateA = new Date(a[props.sortBy])
      const dateB = new Date(b[props.sortBy])
      if (dateA < dateB) return props.sortOrder === 'ASC' ? -1 : 1
      if (dateA > dateB) return props.sortOrder === 'ASC' ? 1 : -1
      return 0
    })

    // Add isDisplayYear flag based on sorted position
    let count = 0;

    for (const post of postsNormalized) {
      count++;
      post.isDisplayYear = post.year !== lastYear || count >= postsNormalized.length
      lastYear = post.year
      postsListDate.push(post)
      if(props.limit && postsListDate.length >= props.limit) {
        break;
      }
    }
    return postsListDate
  } else {
    return props.limit ? postsNormalized.slice(0, props.limit) : postsNormalized
  }
})
</script>

<template>
  <slot :posts="posts">
    <section class="not-prose font-mono mr-2 md:mr-8">
      <div class="grid grid-cols-10 font-light text-xs/7 md:text-lg/9 border-b">
        <div class="col-span-2">date</div>
        <div v-if="isShowFormat" class="hidden md:block md:col-span-2">format</div>
        <div v-if="isShowFormat" class="col-span-8 md:col-span-6">title</div>
        <div v-else class="col-span-8">title</div>
      </div>
      <ul>
        <li v-for="post in posts" :key="post.path">
          <NuxtLink 
            :to="post.path" 
            class="grid grid-cols-10 border-b hover:bg-gray-100 dark:hover:bg-gray-800"
          >
            <div class="col-span-2 text-xs/7 md:text-lg/9 font-light">
              {{ post.isDisplayYear ? post.monthDayYear : post.monthDay }}
            </div>
            <div v-if="isShowFormat" class="hidden md:block md:col-span-2 font-light">
              {{ post.content_type }}
            </div>
            <div class="col-span-8 md:col-span-6 font-semibold">
              {{ post.title }}<sup><span v-if="post.isNew" class="ml-1 text-red-500 font-bold text-xs">updated</span></sup><LinkStatus :published="post.published" />
            </div>
          </NuxtLink>
        </li>
      </ul>
    </section>
  </slot>
</template>

<style scoped>
.column {
  @apply flex items-center space-x-8 py-2 border-b border-gray-200 dark:border-gray-700
}
</style>